/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Link, TextField } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { signupAPI } from '../api/crm'
import { loginUser } from '../store/slices/authSlice'
import { selectTenant } from '../store/slices/tenantSlice'
import { getErrorMessage } from '../utils/apiErrors'
import AuthCard from '../components/common/AuthCard'
import Loading from '../components/common/Loading'

/** From the sign-up email: /signup/verify#token=… — choose a password; the account and workspace are created. */
const SignupVerifyPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // the token is in the #fragment, so it never reaches server logs
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '')
  const preview = useQuery({ queryKey: ['signup', 'preview', token], queryFn: () => signupAPI.preview(token), enabled: !!token, retry: false })
  const [workspaceName, setWorkspaceName] = useState(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [formError, setFormError] = useState(null)

  const complete = useMutation({
    mutationFn: async () => {
      const done = await signupAPI.complete(token, password, (workspaceName ?? preview.data.workspaceName).trim())
      // the account exists now: sign straight in with the password just chosen
      const login = await dispatch(loginUser({ email: done.email, password }))
      if (!loginUser.fulfilled.match(login)) throw new Error('Your account is ready. Please sign in.')
      return done
    },
    onSuccess: (done) => {
      dispatch(selectTenant({ tenantId: done.tenantId, name: done.workspaceName, role: 'TENANT_ADMIN', status: 'ACTIVE' }))
      queryClient.clear()
      navigate('/settings/templates', { replace: true })
    },
  })

  const submit = (e) => {
    e.preventDefault()
    if (password.length < 8) return setFormError('Use at least 8 characters')
    if (password !== confirm) return setFormError('Passwords don’t match')
    setFormError(null)
    complete.mutate()
  }

  if (token && preview.isLoading) return <Loading fullScreen />
  if (!token || preview.error) {
    return (
      <AuthCard subtitle="Finish signing up">
        <Alert severity="error">{token ? getErrorMessage(preview.error) : 'This sign-up link isn’t complete. Open it from the email again.'}</Alert>
        <Link component={RouterLink} to="/signup" variant="body2" sx={{ display: 'inline-block', mt: 2 }}>Sign up again</Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard subtitle="Choose a password to finish">
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(formError || complete.error) && <Alert severity="error">{formError || getErrorMessage(complete.error)}</Alert>}
        <TextField label="Email" value={preview.data.email} InputProps={{ readOnly: true }} autoComplete="username" />
        <TextField label="Workspace name" required value={workspaceName ?? preview.data.workspaceName}
          onChange={(e) => setWorkspaceName(e.target.value)} inputProps={{ maxLength: 100 }} />
        <TextField label="Password" type="password" autoComplete="new-password" required autoFocus value={password}
          onChange={(e) => setPassword(e.target.value)} helperText="At least 8 characters" />
        <TextField label="Confirm password" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button type="submit" variant="contained" disabled={complete.isPending}>
          {complete.isPending ? 'Creating your workspace…' : 'Create account and workspace'}
        </Button>
      </Box>
    </AuthCard>
  )
}

export default SignupVerifyPage
